import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgetCategories,
  type BudgetCategoryRecord,
  type BudgetCategoryType,
} from "@/store/budgetSlice";

/**
 * 支出カテゴリ Single Source of Truth フック
 *
 * 予実 (BudgetCategories テーブル) を支出カテゴリの唯一のマスタとして扱う。
 * - 予実画面の科目行と、支出まとめの CardTab・登録フォームの選択肢で
 *   完全に同じデータを参照する。
 * - 旧 `useMergedCategories({ kind: "expense" })`
 *   (`ClientMasters.expense_categories`) は廃止。
 *
 * 並び順:
 *   - ユーザーが作成した支出明細科目を `display_order` 昇順
 *
 * 関連:
 *   - BE: `src/repositories/budgetCategoryModel.ts` (DDB)
 *   - BE: `src/services/budget/budgetCategoryService.ts`
 *   - BE: `src/services/expense/expenseActualService.ts`
 *     (budget_category_id 優先 → name 一致 fallback の解決順)
 */

export interface ExpenseBudgetCategoryItem {
  category_id: string;
  name: string;
  category_type: BudgetCategoryType;
  /** 支出の上位カテゴリ。CardTab / URL(?category=) 絞り込みに使う。 */
  expense_group?: string;
  /**
   * 親子モデル: "category"=カテゴリ(親, 見出し) / "item"=明細科目(子)。
   * 明細セレクトで親を除外するため伝播が必須。未設定は "item" 相当。
   */
  level?: "category" | "item";
  /** 明細科目(子)の親カテゴリ category_id。 */
  parent_category_id?: string;
  display_order: number;
  is_system: boolean;
}

const EXPENSE_TYPE_SET: ReadonlySet<BudgetCategoryType> = new Set([
  "expense",
  "expense_opex",
  "expense_capex",
]);

function orderForExpenseTabs(
  list: BudgetCategoryRecord[],
): ExpenseBudgetCategoryItem[] {
  // 支出は is_system を見ず、expense_group を持つ明細項目をそのまま返す。
  // expense_group の無いレガシー非system科目(ゴミ)は除外する。
  const expenses = list.filter(
    (c) =>
      EXPENSE_TYPE_SET.has(c.category_type) &&
      !!(c as any).expense_group &&
      !(c as any).deleted_at,
  );
  expenses.sort(
    (a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999),
  );
  return expenses.map((c) => ({
    category_id: c.category_id,
    name: c.name,
    category_type: c.category_type,
    expense_group: (c as any).expense_group,
    level: (c as any).level,
    parent_category_id: (c as any).parent_category_id,
    display_order: c.display_order,
    is_system: c.is_system,
  }));
}

export interface UseExpenseBudgetCategoriesResult {
  /** 支出カテゴリ一覧 (並び順は orderForExpenseTabs に従う) */
  items: ExpenseBudgetCategoryItem[];
  loading: boolean;
  /** category_id から item を引く */
  getById: (id?: string) => ExpenseBudgetCategoryItem | undefined;
  /** name から item を引く (大文字小文字 / 空白を正規化) */
  getByName: (name?: string) => ExpenseBudgetCategoryItem | undefined;
}

const normalizeName = (s: string) => s.replace(/\s+/g, "").trim();

export function useExpenseBudgetCategories(): UseExpenseBudgetCategoriesResult {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading } = useSelector((s: RootState) => s.budgets.categories);

  useEffect(() => {
    // 既に取得済みなら再取得しない (BudgetCategory は頻繁に変わらない想定)
    if (!data || data.length === 0) {
      dispatch(fetchBudgetCategories()).catch(() => {});
    }
  }, [dispatch, data]);

  const items = useMemo(() => orderForExpenseTabs(data), [data]);

  const getById = (id?: string) =>
    id ? items.find((c) => c.category_id === id) : undefined;
  const getByName = (name?: string) =>
    name
      ? items.find((c) => normalizeName(c.name) === normalizeName(name))
      : undefined;

  return { items, loading, getById, getByName };
}
